/**
 * @fileOverView
 * @description Doc Model, pulls current documentation<br>
 * @copyright 2015-present Flatirontek LLC
 **/

const AmpersandModel = require('ampersand-model');
const app = require("ampersand-app");
const dialog = require("../lib/dialog");
const utilities = require('../lib/utilities');

const ServiceAuth = AmpersandModel.extend({

    urlRoot: function(){
        return app.config.baseApiUrl + "/service/auth";
    },

    ajaxConfig: app.utils.setPersonalAuthTokenHeader,

    props: {
        service: 'string'
    },

    session: {
        serviceConfig: 'object',
        isLegacyAuth: 'boolean',
        configLoaded: 'boolean'
    },

    derived: {


        displayName: {
            deps: ['service', 'serviceConfig', 'isLegacyAuth', 'configLoaded'],
            fn: function(){
                let service = "";
                const clientId = app.queryParams.client_id || window.localStorage.getItem('serviceAuth');
                const authType = window.localStorage.getItem('serviceAuthType');
                
                if (authType === 'legacy') {
                    // Legacy OAuth - get from config
                    service = app.config.serviceAuth[clientId]?.displayName || clientId;
                } else if (authType === 'oauth2') {
                    if (this.configLoaded && this.serviceConfig) {
                        // OAuth2 - get from database (only show after config loaded)
                        service = this.serviceConfig.oauth_service_name || clientId;
                    } else {
                        // Don't show anything until config is loaded for OAuth2
                        return "";
                    }
                } else {
                    // Fallback
                    service = clientId || 'Unknown Service';
                }

                return service;
            }
        },

        shortName: {
            deps: ['displayName'],
            fn: function(){
                return utilities.shortAppName(this.displayName);
            }
        },

        consentTitle: {
            deps: ['shortName'],
            fn: function(){
                return this.shortName ? this.shortName + ' wants to access your Vybit account' : '';
            }
        },

        nameNote: {
            deps: ['displayName', 'serviceConfig'],
            fn: function(){
                const selfRegistered = this.serviceConfig && this.serviceConfig.self_registered;
                return selfRegistered && this.displayName ? utilities.unverifiedNameNote(this.displayName) : '';
            }
        },

        abilitiesLabel: {
            deps: ['shortName'],
            fn: function(){
                return (this.shortName || 'This app') + ' will be able to:';
            }
        },

        //the server grants rw to every client, and email only when asked (with OpenID Connect on); auth.js stores scope decoded
        requestsEmail: {
            deps: [],
            fn: function(){
                return (window.localStorage.getItem('scope') || '').split(/\s+/).indexOf('email') !== -1;
            }
        },

        //the host the code is actually sent to, which unlike the app's name the app cannot choose freely
        returnLine: {
            deps: ['service', 'serviceConfig', 'configLoaded'],
            fn: function(){
                try {
                    const uri = this.redirectUri();
                    return uri ? "After you choose, you'll return to " + new URL(uri).host + '.' : '';
                } catch (e) {
                    return '';
                }
            }
        }
    },

    initialize: function() {
        const authType = window.localStorage.getItem('serviceAuthType');
        this.set('isLegacyAuth', authType === 'legacy');
        this.set('configLoaded', authType === 'legacy');
        
        const clientId = app.queryParams.client_id || window.localStorage.getItem('serviceAuth');
        if (clientId) {
            this.set('service', clientId);
        }
        
        const isLegacyClient = clientId && app.config.serviceAuth && app.config.serviceAuth[clientId] !== undefined;
        
        if (authType === 'oauth2' || (!isLegacyClient && clientId)) {
            this.fetchServiceConfig();
        }
    },

    fetchServiceConfig: function() {

        const clientId = app.queryParams.client_id || window.localStorage.getItem('serviceAuth');
        
        if (!clientId) {
            dialog.alert('OAuth client ID not found.');
            return;
        }

        const isFreshRequest = app.queryParams.client_id !== undefined;
        
        if (!isFreshRequest) {
            const dtSet = window.localStorage.getItem('dtSet');
            const serviceAuthType = window.localStorage.getItem('serviceAuthType');
            const oauth2ServiceName = window.localStorage.getItem('oauth2ServiceName');
            
            if (dtSet && serviceAuthType === 'oauth2') {
                const dtNow = Math.floor(Date.now() / 1000);
                const cacheAge = dtNow - parseInt(dtSet);
                
                if (cacheAge < 120) {
                    const redirectUri = app.queryParams.redirect_uri || window.localStorage.getItem('redirect_uri');
                    const responseType = app.queryParams.response_type || window.localStorage.getItem('response_type') || 'code';
                    const state = app.queryParams.state || window.localStorage.getItem('state');
                    
                    if (redirectUri && responseType === 'code' && state &&
                        typeof state === 'string' && state.length >= 8 && state.length <= 2048) {
                        
                        if (oauth2ServiceName) {
                            const config = {
                                oauth_service_name: oauth2ServiceName,
                                oauth_redirect_uri: decodeURIComponent(redirectUri),
                                self_registered: window.localStorage.getItem('oauth2SelfRegistered') === 'true'
                            };
                            
                            this.set('serviceConfig', config);
                            this.set('configLoaded', true);
                            return;
                        }
                    }
                } else {
                    this.clearServiceAuthAndRedirect();
                    return;
                }
            }
        }
        
        const redirectUri = app.queryParams.redirect_uri || window.localStorage.getItem('redirect_uri');
        
        if (!redirectUri) {
            this.failAndRedirect('Invalid service authentication request, missing redirect_uri parameter.');
            return;
        }
        
        // Validate response_type parameter (OAuth 2.0 requirement)
        const responseType = app.queryParams.response_type || window.localStorage.getItem('response_type') || 'code';
        if (responseType !== 'code') {
            this.failAndRedirect('Invalid service authentication request, invalid response_type. Only "code" is supported.');
            return;
        }
        
        // Validate state parameter (OAuth 2.0 security requirement)
        const state = app.queryParams.state || window.localStorage.getItem('state');
        if (!state) {
            this.failAndRedirect('Invalid service authentication request, missing state parameter.');
            return;
        }
        if (typeof state !== 'string' || state.length < 8 || state.length > 2048) {
            this.failAndRedirect('Invalid service authentication request, state parameter must be between 8 and 2048 characters.');
            return;
        }
        
        const safeEncode = (value) => {
            if (!value) return value;
            try {
                return decodeURIComponent(value) === value ? encodeURIComponent(value) : value;
            } catch (e) {
                return encodeURIComponent(value);
            }
        };

        const url = `${app.config.baseApiUrl}/service/config?client_id=${encodeURIComponent(clientId)}&redirect_uri=${safeEncode(redirectUri)}&response_type=${safeEncode(responseType)}&state=${safeEncode(state)}`;
        
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        
        const self = this;
        xhr.onload = function() {
            if (xhr.status === 200) {
                const config = JSON.parse(xhr.responseText);
                self.set('serviceConfig', config);
                self.set('configLoaded', true);
                
                if (config.oauth_service_name) {
                    window.localStorage.setItem('oauth2ServiceName', config.oauth_service_name);
                    window.localStorage.setItem('oauth2SelfRegistered', config.self_registered ? 'true' : 'false');
                }
                
                // Trigger a custom event for UI updates
                self.trigger('serviceNameLoaded', config.oauth_service_name);
            } else if (xhr.status === 404) {
                self.failAndRedirect('Invalid service authentication request, OAuth configuration not found for client ID: ' + clientId);
            } else if (xhr.status === 400) {
                const errorResponse = JSON.parse(xhr.responseText);
                self.failAndRedirect(errorResponse.error || 'Invalid service authentication request, configuration error.');
            } else {
                self.failAndRedirect('Invalid service authentication request, error loading OAuth configuration.');
            }
        };
        
        xhr.onerror = function() {
            self.failAndRedirect('Invalid service authentication request, network error loading OAuth configuration.');
        };
        
        xhr.send();
    },

    processAllowRedirect: function() {

        //alertdesk
        if (this.service == "alertdesk") {
            this.alertDeskRedirect();

            //all others
        } else {
            this.defaultRedirect();
        }
    },

    redirectUri: function(){
        const authType = window.localStorage.getItem('serviceAuthType');
        const stored = window.localStorage.getItem('redirect_uri');
        if (authType === 'legacy') {
            const legacy = app.config.serviceAuth[this.service];
            return stored ? decodeURIComponent(stored) : legacy && legacy.redirect_uri;
        }
        if (authType === 'oauth2' && this.serviceConfig) {
            return stored ? decodeURIComponent(stored) : this.serviceConfig.oauth_redirect_uri;
        }
        return null;
    },

    defaultRedirect: function(){
        const redirectUrl = this.redirectUri();
        if (!redirectUrl) {
            dialog.alert('OAuth configuration error: Unable to determine redirect URI.');
            return;
        }
        
        //read state now: saveTokenAndRedirect clears localStorage before it redirects
        const state = window.localStorage.getItem("state");

        //the server issues a single use code bound to this redirect_uri
        this.saveTokenAndRedirect(redirectUrl, function(code){
            const separator = redirectUrl.indexOf('?') === -1 ? '?' : '&';
            return redirectUrl + `${separator}state=${state}&code=${code}`;
        });
    },

    alertDeskRedirect: function(){
        const storedRedirectUri = window.localStorage.getItem("redirect_uri");
        const redirectUrl = storedRedirectUri ? 
            decodeURIComponent(storedRedirectUri) : 
            app.config.serviceAuth[this.service].redirect_uri;
        const queryString = `?client_id=${app.config.serviceAuth.alertdesk.clientId}&redirect_uri=${app.config.restApiUrl}/servicetoken`;
        
        this.saveTokenAndRedirect(redirectUrl + queryString, () => redirectUrl + queryString);
    },

    saveTokenAndRedirect: function(redirectUri, buildRedirectUrl){
        const responseType = app.queryParams.response_type || window.localStorage.getItem('response_type') || 'code';
        const data = {
            service: this.service,
            response_type: responseType,
            redirect_uri: redirectUri,
            code_challenge: localStorage.getItem('code_challenge'),
            code_challenge_method: localStorage.getItem('code_challenge_method')
        };
        ['scope', 'nonce'].forEach(key => {
            const value = localStorage.getItem(key);
            if (value) data[key] = value;
        });
        
        this.fetch({
            data: data,
            success: function(model,response){
                //build the redirect before clearing, it reads from localStorage
                const redirectUrl = buildRedirectUrl(response.code);
                window.localStorage.clear();
                app.queryParams = {};
                if(app.platform === "app") app.loadData();
                window.location.href = redirectUrl;
            },
            error: function(model,response){
                console.log('error in serviceauth', response.body);
            }
        });
    },

    //the redirect waits for the message to be dismissed, which a native alert did by blocking
    failAndRedirect: function(message) {
        return dialog.alert(message).then(() => this.clearServiceAuthAndRedirect());
    },

    clearServiceAuthAndRedirect: function() {
        utilities.serviceAuthKeys.forEach(key => window.localStorage.removeItem(key));

        app.serviceAuth = false;
        app.queryParams = {};

        window.location.href = '/';
    }

});

module.exports = ServiceAuth;
